import { useMemo, useState } from "react";
import {
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClipboardDocumentCheckIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ShieldCheckIcon,
    ShieldExclamationIcon,
} from "@heroicons/react/24/outline";

import { formatProviderSubtitle, formatProviderTitle } from "@/lib/cvImportProvider";
import { getWarningUserMessage } from "@/lib/cvWarningMessages";
import { isAnomalyActive, computeDocumentQualityProgress } from "@/stores/cvBuilderStore";
import QualityControlPanel from "./QualityControlPanel";

const ConfidenceScoreView = ({
    score = 0,
    warnings = [],
    structuredWarnings = [],
    sectionCoverage = null,
    anomalies = [],
    scoringDimensions = null,
    mandatoryReview = false,
    validationStatus,
    verificationProgress: propVerificationProgress = null,
    documentQuality = null,
    providerPrimary,
    providerUsed,
    providerFallbackUsed,
    modelUsed,
    onReview,
    onResolveAnomaly = () => {},
    onNavigateToStep = () => {},
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [isQualityControlOpen, setIsQualityControlOpen] = useState(false);

    const safeAnomalies = useMemo(() => (Array.isArray(anomalies) ? anomalies : []), [anomalies]);

    const activeAnomalies = useMemo(
        () => safeAnomalies.filter((item) => isAnomalyActive(item?.status)),
        [safeAnomalies],
    );

    const verificationProgress = useMemo(
        () => computeDocumentQualityProgress(documentQuality, propVerificationProgress, safeAnomalies),
        [documentQuality, propVerificationProgress, safeAnomalies],
    );

    const reviewCount = activeAnomalies.length || (safeAnomalies.length === 0 ? warnings.length : 0);
    const resolvedStatus = ["READY", "READY_WITH_WARNINGS", "REVIEW_RECOMMENDED", "CORRECTION_REQUIRED", "BLOCKED"].includes(validationStatus)
        ? validationStatus
        : (mandatoryReview ? "CORRECTION_REQUIRED" : (reviewCount > 0 ? "REVIEW_RECOMMENDED" : "READY"));

    const fidelitySubtitle = useMemo(() => {
        if (score >= 90) return "Extraction très fidèle";
        if (score >= 75) return "Extraction satisfaisante";
        if (score > 0) return "Extraction partielle";
        return "Évaluation non disponible";
    }, [score]);

    const hasSignificantLoss = useMemo(() => {
        return safeAnomalies.some(
            (a) => a.category === "missing_significant_content" && isAnomalyActive(a.status)
        );
    }, [safeAnomalies]);

    const isEditedAfterImport = documentQuality?.documentary_verification_current === false || documentQuality?.decision === "TECHNICAL_INTEGRITY_ONLY";

    const baseStatusConfig = {
        READY: isEditedAfterImport ? {
            badge: "Intégrité technique validée (Édité)",
            title: "CV modifié et validé techniquement",
            description: "Le CV a été modifié et revalidé techniquement. La vérification documentaire d'origine n'est plus courante.",
            tone: "blue",
            icon: CheckCircleIcon,
        } : {
            badge: "Prêt à générer",
            title: "CV prêt à être utilisé",
            description: "Aucune anomalie critique non résolue. Les informations principales du document ont été correctement structurées.",
            tone: "emerald",
            icon: CheckCircleIcon,
        },
        REVIEW_RECOMMENDED: {
            badge: "Vérification recommandée",
            title: "Quelques points à vérifier",
            description: `${reviewCount} information${reviewCount > 1 ? "s" : ""} nécessite${reviewCount > 1 ? "nt" : ""} une vérification rapide. La prévisualisation et l’export restent disponibles.`,
            tone: "amber",
            icon: ExclamationTriangleIcon,
        },
        READY_WITH_WARNINGS: {
            badge: "Import partiel",
            title: "CV prêt à compléter",
            description: "Le brouillon exploitable est disponible. Certaines informations doivent être vérifiées dans le CV Builder.",
            tone: "amber",
            icon: ExclamationTriangleIcon,
        },
        CORRECTION_REQUIRED: {
            badge: "Relecture recommandée",
            title: "Points importants à relire",
            description: "Certaines informations méritent une relecture. Elles restent visibles et n’empêchent ni la prévisualisation ni l’export.",
            tone: "rose",
            icon: ShieldExclamationIcon,
        },
        BLOCKED: {
            badge: "Contradiction critique",
            title: "Import bloqué",
            description: "Une contradiction documentaire critique doit être résolue avant d’utiliser ce CV.",
            tone: "rose",
            icon: ShieldExclamationIcon,
        },
    };
    const statusConfig = baseStatusConfig[resolvedStatus] || baseStatusConfig.READY;

    const toneClasses = {
        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
        blue: "border-blue-500/25 bg-blue-500/10 text-blue-300",
        amber: "border-amber-500/25 bg-amber-500/10 text-amber-200",
        rose: "border-rose-500/25 bg-rose-500/10 text-rose-200",
    }[statusConfig.tone] || "border-blue-500/25 bg-blue-500/10 text-blue-300";

    const StatusIcon = statusConfig.icon;

    const experienceCoverage = sectionCoverage?.experiences;
    const extractedExperiences = experienceCoverage?.extracted_count;
    const identifiedExperiences = experienceCoverage?.identified_count;
    const experienceSummary = Number.isFinite(extractedExperiences)
        ? `${extractedExperiences}${Number.isFinite(identifiedExperiences) ? `/${identifiedExperiences}` : ""} expérience${extractedExperiences > 1 ? "s" : ""} correctement extraite${extractedExperiences > 1 ? "s" : ""}`
        : "Expériences professionnelles structurées";

    const recoveredSections = [
        sectionCoverage?.education?.extracted_present && "formations",
        sectionCoverage?.languages?.extracted_present && "langues",
        sectionCoverage?.certifications?.extracted_present && "certifications",
    ].filter(Boolean);
    const sectionsSummary = recoveredSections.length > 0
        ? `${recoveredSections.join(" et ")} récupérées`
        : "Formations et langues récupérées";

    const lossSummary = hasSignificantLoss
        ? "Information importante à vérifier"
        : "Aucune perte significative détectée";

    const detailItems = (structuredWarnings?.length ? structuredWarnings : warnings)
        .map((item) => getWarningUserMessage(item))
        .filter(Boolean);

    const providerTitle = formatProviderTitle(providerUsed || providerPrimary);
    const providerSubtitle = formatProviderSubtitle(modelUsed, providerFallbackUsed);

    const handlePrimaryAction = () => {
        onReview?.();
    };

    return (
        <section className="mx-auto flex w-full max-w-4xl justify-center px-4 py-6" aria-labelledby="cv-analysis-title">
            <div className="w-full space-y-6 rounded-3xl border border-blue-500/20 bg-slate-900/95 p-6 shadow-2xl shadow-blue-950/20 sm:p-8">
                {/* Header */}
                <div className="text-center">
                    <p id="cv-analysis-title" className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
                        Analyse du CV
                    </p>
                    <h1 className="mt-1 text-xl font-bold tracking-tight text-white sm:text-2xl">
                        Rapport de conformité documentaire
                    </h1>
                </div>

                {/* 3 Pillars Grid: FIDÉLITÉ, VÉRIFICATION, STATUT */}
                <div className="grid gap-4 md:grid-cols-3">
                    {/* INDICATEUR A : FIDÉLITÉ DE L'EXTRACTION */}
                    <div className="flex flex-col justify-between rounded-2xl border border-blue-500/25 bg-slate-950/70 p-5 text-left shadow-inner">
                        <div>
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-400">
                                    Fidélité de l'extraction
                                </span>
                                <ShieldCheckIcon className="h-4 w-4 text-blue-400" aria-hidden="true" />
                            </div>
                            <div className="mt-3 flex items-baseline gap-2">
                                <span className="text-3xl font-extrabold tracking-tight text-white" aria-label={`Score d'extraction : ${score} pour cent`}>
                                    {score}%
                                </span>
                                <span className="text-xs font-semibold text-blue-300">
                                    {fidelitySubtitle}
                                </span>
                            </div>
                            {documentQuality ? (
                                <p className="mt-2 text-xs font-medium text-blue-200">
                                    Votre CV a été analysé et corrigé automatiquement.
                                </p>
                            ) : null}
                            <p className="mt-1 text-xs text-slate-400">
                                Mesure technique de correspondance source → JSON.
                            </p>
                        </div>

                        <div className="mt-4 space-y-2 border-t border-slate-800/80 pt-3 text-xs text-slate-300">
                            <div className="flex items-start gap-1.5">
                                <CheckCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" aria-hidden="true" />
                                <span>{experienceSummary}</span>
                            </div>
                            <div className="flex items-start gap-1.5">
                                <CheckCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" aria-hidden="true" />
                                <span>{sectionsSummary}</span>
                            </div>
                            <div className="flex items-start gap-1.5">
                                {hasSignificantLoss ? (
                                    <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" aria-hidden="true" />
                                ) : (
                                    <CheckCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" aria-hidden="true" />
                                )}
                                <span className={hasSignificantLoss ? "text-rose-300 font-medium" : ""}>
                                    {lossSummary}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* INDICATEUR B : VÉRIFICATION AUTOMATIQUE */}
                    <div className="flex flex-col justify-between rounded-2xl border border-blue-500/25 bg-slate-950/70 p-5 text-left shadow-inner">
                        <div>
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-400">
                                    Traitement automatique par IA
                                </span>
                                <ClipboardDocumentCheckIcon className="h-4 w-4 text-blue-400" aria-hidden="true" />
                            </div>
                            <div className="mt-3 flex items-baseline gap-2">
                                <span className="text-2xl font-extrabold tracking-tight text-white">
                                    {verificationProgress.treated}/{verificationProgress.total}
                                </span>
                                <span className="text-xs font-medium text-slate-300">
                                    décisions automatiques
                                </span>
                            </div>
                            {/* Progress bar */}
                            <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-800" role="progressbar" aria-valuenow={verificationProgress.percentage} aria-valuemin="0" aria-valuemax="100">
                                <div
                                    className="h-full rounded-full bg-blue-500 transition-all duration-300"
                                    style={{ width: `${verificationProgress.percentage}%` }}
                                />
                            </div>
                            <p className="mt-2 text-xs text-slate-300">
                                {documentQuality
                                    ? `${documentQuality.corrections_applied || 0} correction(s) appliquée(s) • ${documentQuality.anomalies_unresolved ?? documentQuality.uncertain_facts ?? 0} information(s) non confirmée(s) • aucune validation individuelle requise`
                                    : (verificationProgress.remaining > 0
                                        ? `${verificationProgress.remaining} point${verificationProgress.remaining > 1 ? "s" : ""} non traité${verificationProgress.remaining > 1 ? "s" : ""}`
                                        : "Tous les faits ont été vérifiés automatiquement")}
                            </p>
                            {documentQuality?.counts ? (
                                <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                                    {documentQuality.automatically_verified || 0} vérifié(s) • {documentQuality.automatically_decided ?? documentQuality.total_facts ?? 0} décision(s) automatique(s) • {documentQuality.anomalies_unresolved || 0} non confirmé(s)
                                </p>
                            ) : null}
                        </div>

                        <div className="mt-4 border-t border-slate-800/80 pt-3">
                            <button
                                type="button"
                                onClick={() => setIsQualityControlOpen(true)}
                                className="w-full min-h-9 rounded-xl border border-blue-500/30 bg-blue-950/40 px-3 py-2 text-xs font-semibold text-blue-200 transition-colors hover:bg-blue-900/50 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                            >
                                {documentQuality ? "Historique des corrections IA" : "Lancer la vérification IA"}
                            </button>
                        </div>
                    </div>

                    {/* INDICATEUR C : STATUT DU CV */}
                    <div className="flex flex-col justify-between rounded-2xl border border-blue-500/25 bg-slate-950/70 p-5 text-left shadow-inner">
                        <div>
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-blue-400">
                                    Statut du CV
                                </span>
                                <StatusIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                            </div>
                            <div className="mt-3">
                                <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${toneClasses}`} role="status">
                                    <StatusIcon className="h-4 w-4" aria-hidden="true" />
                                    <span>{statusConfig.badge}</span>
                                </div>
                            </div>
                            <h2 className="mt-2 text-sm font-semibold text-white">
                                {statusConfig.title}
                            </h2>
                            <p className="mt-1 text-xs leading-relaxed text-slate-300">
                                {statusConfig.description}
                            </p>
                        </div>

                        <div className="mt-4 border-t border-slate-800/80 pt-3">
                            <span className="text-[11px] text-slate-400">
                                Export toujours disponible • Contrôles de sécurité maintenus
                            </span>
                        </div>
                    </div>
                </div>

                {/* Primary Action Buttons */}
                <div className="mx-auto flex max-w-xl flex-col items-stretch justify-center gap-3 pt-2 sm:flex-row">
                    <button
                        type="button"
                        onClick={handlePrimaryAction}
                        className="min-h-11 rounded-xl bg-blue-600 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-950/40 transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
                    >
                        Accéder au CV Builder
                    </button>
                    {resolvedStatus === "REVIEW_RECOMMENDED" && reviewCount > 0 ? (
                        <button
                            type="button"
                            onClick={() => setIsQualityControlOpen(true)}
                            className="min-h-11 rounded-xl border border-slate-700 bg-slate-800 px-6 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
                        >
                            Examiner les {reviewCount} point{reviewCount > 1 ? "s" : ""}
                        </button>
                    ) : null}
                    {resolvedStatus === "CORRECTION_REQUIRED" ? (
                        <button
                            type="button"
                            onClick={onReview}
                            className="min-h-11 rounded-xl border border-slate-700 bg-slate-800 px-6 py-3 text-sm font-semibold text-slate-100 transition-colors hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
                        >
                            Modifier le CV
                        </button>
                    ) : null}
                </div>

                {/* Technical Details Collapsible */}
                <div className="border-t border-slate-800/80 pt-4 text-left">
                    <button
                        type="button"
                        aria-expanded={showDetails}
                        aria-controls="cv-analysis-details"
                        onClick={() => setShowDetails((value) => !value)}
                        className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-blue-300 hover:bg-blue-950/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
                    >
                        {showDetails ? "Masquer les détails de l’analyse" : "Voir les détails de l’analyse"}
                        {showDetails ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />}
                    </button>
                    {showDetails ? (
                        <div id="cv-analysis-details" className="mt-3 space-y-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4 sm:p-5">
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                                <span>Moteur d’analyse : {providerTitle}</span>
                                {providerSubtitle ? <span>{providerSubtitle}</span> : null}
                            </div>
                            {detailItems.length > 0 ? (
                                <ul className="space-y-2 text-sm text-slate-300">
                                    {detailItems.slice(0, 5).map((message, index) => (
                                        <li key={`${message}-${index}`} className="flex gap-2">
                                            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />
                                            <span>{message}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-slate-400">Aucun point supplémentaire à signaler.</p>
                            )}
                            {reviewCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => setIsQualityControlOpen(true)}
                                    className="min-h-11 w-full rounded-xl border border-blue-500/30 bg-blue-950/30 px-4 text-sm font-semibold text-blue-200 hover:bg-blue-950/50"
                                >
                                    Ouvrir le contrôle détaillé
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>

            <QualityControlPanel
                isOpen={isQualityControlOpen}
                onClose={() => setIsQualityControlOpen(false)}
                anomalies={safeAnomalies}
                scoringDimensions={scoringDimensions}
                totalScore={score}
                verificationProgress={verificationProgress}
                documentQuality={documentQuality}
                onResolveAnomaly={onResolveAnomaly}
                onNavigateToStep={onNavigateToStep}
            />
        </section>
    );
};

export default ConfidenceScoreView;
